import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

export interface PaginationValue {
  page: number;
  pageSize: number;
  sortBy?: string;
  sortOrder?: string;
}

export type PageType =
  | 'complaints'
  | 'customers'
  | 'employers'
  | 'uploads'
  | 'users'
  | 'chats'
  | 'planChangeRequests'
  | 'withdrawalRequests'
  | 'transferInRequests'
  | 'transferOutRequests'
  | 'transfers'
  | 'employees'
  | 'contributions'
  | 'customerAudit'
  | 'employeeAudit'
  | 'chatAudit'
  | 'complaintAudit'
  | 'transactionAudit'
  | 'dailyFee'
  | 'dailyFeeModal'
  | 'monthlyFee'
  | 'monthlyFeeUnitsModal'
  | 'salesOrder'
  | 'salesOrderDetailed'
  | 'todayOrderDetailed'
  | 'allTransaction'
  | 'contribution history'
  | 'todayOrders'
  | 'orderHistory'
  | 'userWiseContribution'
  | 'fundDetails'
  | 'mmTransaction'
  | 'holdings'
  | 'mmUnitSales'
  | 'uploadDetails'
  | 'uploadDetailsNonReconciledStatus'
  | 'uploadDetailsOtherPaymentsStatus'
  | 'ordersDetailed'
  | 'ordersSummary'
  | 'bankReport'
  | 'currentHoldings'
  | 'dailyFundPrice'
  | 'replacingPolicyRequestStatus'
  | 'avcRequestsStatus'
  | 'userAllocationInfo'
  | 'userContributions'
  | 'unitsReallocation';

export type IPagination = Record<PageType, PaginationValue>;

interface pageActionPayloadType {
  key: PageType;
  value: number | string | undefined | null;
}

const initialState: IPagination = {
  complaints: { page: 0, pageSize: 10 },
  customers: { page: 0, pageSize: 10, sortBy: '', sortOrder: '' },
  replacingPolicyRequestStatus: {
    page: 0,
    pageSize: 10,
    sortBy: '',
    sortOrder: '',
  },
  avcRequestsStatus: {
    page: 0,
    pageSize: 10,
    sortBy: '',
    sortOrder: '',
  },
  employers: {
    page: 0,
    pageSize: 10,
    sortBy: '',
    sortOrder: '',
  },
  mmUnitSales: {
    page: 0,
    pageSize: 10,
    sortBy: '',
    sortOrder: '',
  },
  uploads: { page: 0, pageSize: 10 },
  users: { page: 0, pageSize: 10, sortBy: '', sortOrder: '' },
  chats: { page: 0, pageSize: 10 },
  planChangeRequests: { page: 0, pageSize: 10 },
  withdrawalRequests: { page: 0, pageSize: 10 },
  transferInRequests: { page: 0, pageSize: 10 },
  transferOutRequests: { page: 0, pageSize: 10 },
  transfers: { page: 0, pageSize: 10 },
  employees: { page: 0, pageSize: 10, sortBy: '', sortOrder: '' },
  salesOrder: { page: 0, pageSize: 10 },
  salesOrderDetailed: { page: 0, pageSize: 10 },
  todayOrderDetailed: { page: 0, pageSize: 10 },
  allTransaction: { page: 0, pageSize: 10 },
  contributions: { page: 0, pageSize: 10 },
  todayOrders: { page: 0, pageSize: 10 },
  orderHistory: { page: 0, pageSize: 10 },
  userWiseContribution: { page: 0, pageSize: 10 },
  fundDetails: { page: 0, pageSize: 10 },
  mmTransaction: { page: 0, pageSize: 10 },
  dailyFee: { page: 0, pageSize: 10 },
  dailyFeeModal: { page: 0, pageSize: 10 },
  monthlyFee: { page: 0, pageSize: 10 },
  monthlyFeeUnitsModal: { page: 0, pageSize: 10 },
  holdings: { page: 0, pageSize: 10 },
  chatAudit: { page: 0, pageSize: 10 },
  customerAudit: { page: 0, pageSize: 10 },
  complaintAudit: { page: 0, pageSize: 10 },
  employeeAudit: { page: 0, pageSize: 10 },
  transactionAudit: { page: 0, pageSize: 10 },
  uploadDetails: { page: 0, pageSize: 10 },
  uploadDetailsNonReconciledStatus: { page: 0, pageSize: 10 },
  uploadDetailsOtherPaymentsStatus: { page: 0, pageSize: 10 },
  ordersDetailed: { page: 0, pageSize: 10 },
  bankReport: { page: 0, pageSize: 10 },
  ordersSummary: { page: 0, pageSize: 10 },
  currentHoldings: { page: 0, pageSize: 10 },
  dailyFundPrice: { page: 0, pageSize: 10 },
  userAllocationInfo: { page: 0, pageSize: 10 },
  userContributions: { page: 0, pageSize: 10 },
  unitsReallocation: { page: 0, pageSize: 10 },
};

const paginationSlice = createSlice({
  name: 'pagination',
  initialState,
  reducers: {
    setCurrentPage: (
      state,
      { payload }: PayloadAction<pageActionPayloadType>,
    ) => ({
      ...state,
      [payload.key]: { ...state[payload.key], page: payload.value },
    }),
    setRowsPerPage: (
      state,
      { payload }: PayloadAction<pageActionPayloadType>,
    ) => ({
      ...state,
      [payload.key]: { ...state[payload.key], pageSize: payload.value },
    }),
    setSortBy: (state, { payload }: PayloadAction<pageActionPayloadType>) => ({
      ...state,
      [payload.key]: { ...state[payload.key], sortBy: payload.value },
    }),
    setSortOrder: (
      state,
      { payload }: PayloadAction<pageActionPayloadType>,
    ) => ({
      ...state,
      [payload.key]: { ...state[payload.key], sortOrder: payload.value },
    }),
  },
});

export const { setCurrentPage, setRowsPerPage, setSortBy, setSortOrder } =
  paginationSlice.actions;

export default paginationSlice.reducer;
